import { rememberOssImages, setOssSession } from '@/utils/ossImageAccess'
import { ossPayloadForSave } from '@/utils/oss'
import service from './service'
import { CONTENT_TYPE } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import {
  AdminSessionExpiredError,
  ensureAdminSession,
  readAdminSession
} from '@/utils/adminSession'
import { ElMessage } from 'element-plus'

const request = async (option: AxiosConfig): Promise<unknown> => {
  const { url, method, params, data, headers, responseType } = option

  const userStore = useUserStoreWithOut()
  const managesSession =
    url?.startsWith('/adminApi/') &&
    !['/adminApi/auth/login', '/adminApi/auth/refresh', '/adminApi/auth/logout'].includes(url) &&
    !Object.keys(headers || {}).some((key) => key.toLowerCase() === 'authorization')
  const owner = managesSession ? readAdminSession()?.sessionId || '' : userStore.getSessionIdentity
  try {
    if (managesSession) await ensureAdminSession()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '登录续期失败，请稍后重试')
    throw error
  }
  setOssSession(owner)
  const query = params ? { ...params } : undefined
  const isRead = method?.toLowerCase() === 'get'
  for (let attempt = 0; ; attempt++) {
    if (managesSession && (readAdminSession()?.sessionId || '') !== owner)
      throw new Error('登录状态已变更，请重试')
    const token = userStore.getToken
    let response: unknown
    try {
      response = await service.request({
        url: url,
        method,
        params,
        data: isRead ? data : ossPayloadForSave(data, /companyGoods/i.test(url || '')),
        responseType: responseType,
        headers: {
          'Content-Type': CONTENT_TYPE,
          [userStore.getTokenKey ?? 'Authorization']: token ? `Bearer ${token}` : '',
          ...headers
        }
      })
    } catch (error) {
      if (!(error instanceof AdminSessionExpiredError)) throw error
      // 只重放服务端在执行业务前明确拒绝的请求；HTTP 401、超时和网络故障不重放。
      if (managesSession && attempt === 0 && owner && readAdminSession()?.sessionId === owner) {
        try {
          await ensureAdminSession(token)
        } catch (refreshError) {
          ElMessage.error(refreshError instanceof Error ? refreshError.message : '登录续期失败')
          throw refreshError
        }
        continue
      }
      if (managesSession && !owner && !readAdminSession()) userStore.logout()
      ElMessage.error(error.message)
      throw error
    }
    if (managesSession && (readAdminSession()?.sessionId || '') !== owner)
      throw new Error('登录状态已变更，请重试')
    const result = response as
      | (IResponse<unknown> & { ossImagePreviews?: Record<string, string> })
      | undefined
    if (isRead && result?.code === 0 && url?.startsWith('/adminApi/')) {
      rememberOssImages(
        result.data,
        JSON.stringify([url, query]),
        () => request({ ...option, params: query }),
        () => userStore.getSessionIdentity === owner,
        result.ossImagePreviews
      )
    }
    return response
  }
}

export default {
  get: <T = any>(option: AxiosConfig) => {
    return request({ method: 'get', ...option }) as Promise<IResponse<T>>
  },
  post: <T = any>(option: AxiosConfig) => {
    return request({ method: 'post', ...option }) as Promise<IResponse<T>>
  },
  delete: <T = any>(option: AxiosConfig) => {
    return request({ method: 'delete', ...option }) as Promise<IResponse<T>>
  },
  put: <T = any>(option: AxiosConfig) => {
    return request({ method: 'put', ...option }) as Promise<IResponse<T>>
  },
  cancelRequest: (url: string | string[]) => {
    return service.cancelRequest(url)
  },
  cancelAllRequest: () => {
    return service.cancelAllRequest()
  }
}
